import { Alert, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { ConfirmDialog, Dialog, TextEditor } from '../../components';
import { useApiErrorHandler, useResponsiveText } from '../../hooks';
import { innerText } from '../../util';
import { ThingWithContractData } from './contract-management.types';

interface TerminateContractDialogProps {
  open: boolean;
  onClose: () => void;
  item: ThingWithContractData;
  refreshList: VoidFunction;
}

const StyledAlert = styled(Alert)({
  alignItems: 'center',
});

export const ContractManagementTerminateContractDialog: FC<TerminateContractDialogProps> = ({
  open,
  onClose,
  item,
  refreshList,
}) => {
  const { t } = useTranslation(['common', 'data-subscription']);
  const { restClient } = useAuthenticated();
  const { handleUpdateError } = useApiErrorHandler();
  const { enqueueSnackbar } = useSnackbar();

  const warningText = t('common:page.contract-management.warning.labels');
  const h5Styles = useResponsiveText('1.5rem', innerText(warningText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  const [disabled, setDisabled] = useState(false);
  const [terminateNotes, setTerminateNotes] = useState('');

  const handleTerminateContract = async (): Promise<void> => {
    if (!item.contract.dataSubscription?.lastDataSubscription?.dataSubscriptionId) return;
    setDisabled(true);
    try {
      await restClient.admin.dataSubscription.finish(item.contract.dataSubscription.lastDataSubscription.dataSubscriptionId, {
        finish: new Date(),
        reason: 'manual/subscription-expiration',
        notes: terminateNotes,
      });
      enqueueSnackbar(t('common:page.contract-management.contract-terminated-successfully.labels'), { variant: 'success' });
      refreshList();
    }
    catch (err) {
      handleUpdateError(err, 'CONTRACT', item._id);
    } finally {
      setDisabled(false);
      onClose();
    }
  };

  return (
    <Dialog
      actions={
        <ConfirmDialog
          buttonDisabled={disabled || terminateNotes.trim().length < 1}
          confirmPrompt={<Typography variant="body2">{t('common:page.contract-management.terminate-contract.labels')}</Typography>}
          initialPrompt={t('common:page.contract-management.terminate-contract.action.labels')}
          onConfirm={handleTerminateContract}
          buttonVariant="contained"
        />
      }
      data-testid="contract-management-manually-terminate-contract-dialog"
      open={open}
      onClose={onClose}
      hasCloseBtn={true}
      title={t('common:page.contract-management.terminate-contract.labels')}
    >
      <Stack spacing={2}>
        <StyledAlert severity="warning" variant="filled">
          <Typography
            variant="h5"
            textTransform="uppercase"
            style={{
              fontSize: h5Styles.fontSize,
              lineHeight: h5Styles.lineHeight,
              wordBreak: h5Styles.wordBreak,
              whiteSpace: h5Styles.whiteSpace,
              overflow: h5Styles.overflow,
              textOverflow: h5Styles.textOverflow,
            }}
          >
            {warningText}
          </Typography>
          {t('common:page.contract-management.terminate-contract-warning.labels')}
        </StyledAlert>

        <TextEditor
          data-testid="terminate-contract-notes-input"
          label={t('common:page.contract-management.terminate-contract-warning-placeholder.labels')}
          onKeyUp={(value) => setTerminateNotes(value)}
          multiline
          rows={4}
          required
          size="small"
          value={terminateNotes}
        />
      </Stack>
    </Dialog>
  );
};
